import { FileDownloadOutlined } from '@mui/icons-material'
import { Skeleton } from 'antd'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useQueryClient } from 'react-query'
import { useNavigate, useParams } from 'react-router'
import { toast } from 'react-toastify'
import styled from 'styled-components'

import { cardBorder, darkBlueFontColor } from 'assets/styles/variables'
import { deriveCustomBreakdowns } from 'features/charts-v2/derive-custom-breakdowns'
import type { FilterUIState } from 'features/filters/store'
import { useFilterUIStore } from 'features/filters/store'
import { useTopBar } from 'features/project/partials/project-layout/use-layout'
import { useProjectStore } from 'features/project/store'
import {
  BreakdownSection,
  VerbatimsSection,
} from 'features/trend-detection/components/DetailPageSections'
import {
  ElevationLevelPopover,
  type TierConfig,
} from 'features/trend-detection/components/ElevationLevelPopover'
import {
  CardActions,
  CardHeader,
  CardIconButton,
  CardTitle,
  ChartModeButton,
  ChartModeButtonGroup,
  ContentWrapper,
  DetectionBadgeRow,
  DetectionLabels,
  DetectionTitle,
  GlanceColumns,
  GlancePanel,
  GlancePanelDivider,
  InsightDescription,
  InsightHeader,
  InsightTitle,
  PageContainer,
  PageShell,
  renderWithBoldMetrics,
  SectionCard,
  TakeawayItem,
  TakeawayList,
} from 'features/trend-detection/components/insight-detail-styles'
import { InsightDetailSubHeader } from 'features/trend-detection/components/InsightDetailSubHeader'
import {
  EMERGENCE_BOX_BG,
  EMERGENCE_BOX_BORDER,
  EMERGENCE_COLORS,
  EMERGENCE_ICON_COLOR,
} from 'features/trend-detection/components/issue-constants'
import { IssueAgentMetaRow } from 'features/trend-detection/components/IssueAgentMetaRow'
import IssueFeedbackModal from 'features/trend-detection/components/IssueFeedbackModal'
import {
  DetectedIssueIcon,
  EmergenceBarChart,
  SafetyIndicator,
} from 'features/trend-detection/components/IssueShared'
import {
  type ChartMode,
  IssueTimeseriesChart,
} from 'features/trend-detection/components/IssueTimeseriesChart'
import {
  ChartBarSkeleton,
  ChartContainer,
  ChartLoadingContainer,
  NoChartData,
} from 'features/trend-detection/components/SimpleChart'
import { TrackEmailOptInModal } from 'features/trend-detection/components/track-email-opt-in-modal'
import { useIssueFeedback } from 'features/trend-detection/components/use-issue-feedback'
import { useIssueDetail } from 'features/trend-detection/features/issue-detail/hooks/use-issue-detail'
import { useTrackEmailOptInPrompt } from 'features/trend-detection/hooks/use-track-email-opt-in-prompt'
import {
  ISSUE_DETECTION_AGENT_TYPE,
  ResearchAgentsAction,
  trackResearchAgents,
} from 'features/trend-detection/mixpanel'
import { trendDetectionFeedPath } from 'features/trend-detection/paths'
import { useTrendDetectionService } from 'features/trend-detection/service'
import type { TrendFeedIssueItem } from 'features/trend-detection/types'
import type { IssueBreakdownSelectionValue } from 'features/trend-detection/utils/dimension-mappings'
import { downloadChartAsPng } from 'features/trend-detection/utils/download-chart'
import {
  type EmergenceLevel,
  formatOriginalAnalysisPeriod,
  formatUtcDateFromISO,
  getEmergenceLevel,
} from 'features/trend-detection/utils/helpers'
import type { ProjectState } from 'types/project'

type ActiveEmergenceLevel = Exclude<EmergenceLevel, 'nothing'>

const EMERGENCE_TIERS: TierConfig<ActiveEmergenceLevel>[] = (
  [
    ['slightly_elevated', 'SLIGHTLY ELEVATED'],
    ['elevated', 'ELEVATED'],
    ['sharply_elevated', 'SHARPLY ELEVATED'],
  ] as const
).map(([key, label]) => ({
  key,
  label,
  color: EMERGENCE_COLORS[key],
  icon: (active: boolean) => (
    <EmergenceBarChartForLevel level={key} active={active} />
  ),
}))

const EmergenceBarChartForLevel: React.FC<{
  level: ActiveEmergenceLevel
  active: boolean
}> = ({ level, active }) => {
  const c = active ? EMERGENCE_COLORS[level] : '#d9d9d9'
  const heights: Record<string, [number, number, number]> = {
    slightly_elevated: [10, 6, 2],
    elevated: [8, 4, 0],
    sharply_elevated: [6, 2, 0],
  }
  const [y1, y2, y3] = heights[level] ?? [10, 6, 2]
  return (
    <svg
      width={24}
      height={16}
      viewBox="0 0 24 16"
      style={{ display: 'block' }}
    >
      <rect
        x={2}
        y={y1}
        width={5}
        height={16 - y1}
        fill={active ? c : '#d9d9d9'}
        rx={1}
      />
      <rect
        x={9}
        y={y2}
        width={5}
        height={16 - y2}
        fill={level !== 'slightly_elevated' && active ? c : '#d9d9d9'}
        rx={1}
      />
      <rect
        x={16}
        y={y3}
        width={5}
        height={16 - y3}
        fill={level === 'sharply_elevated' && active ? c : '#d9d9d9'}
        rx={1}
      />
    </svg>
  )
}

export const IssueDetailPage: React.FC = () => {
  const { issueUuid } = useParams<{ issueUuid: string }>()
  const projectId = useProjectStore((s: ProjectState) => s.projectId)
  const navigate = useNavigate()
  const service = useTrendDetectionService()
  const { setBreadcrumbSubtitle } = useTopBar()

  const detail = useIssueDetail(service, projectId, issueUuid ?? null)
  const { issueItem, issueCard } = detail
  const issue = issueItem?.issue ?? null

  const emailPrompt = useTrackEmailOptInPrompt(projectId ?? '')
  // Stable across renders (memoized in the hook); depend on it directly rather
  // than the whole `emailPrompt` object, which is a fresh reference every render.
  const { handleTrackResponse: handleEmailPromptResponse } = emailPrompt

  // Page view — fires once per mount so direct-link visits (email, share) are captured.
  // Gate on loaded issueItem so the event carries the issue title and agent context.
  const issueDetailViewFiredRef = useRef(false)
  useEffect(() => {
    if (!issueUuid || !issue || !issueItem || issueDetailViewFiredRef.current)
      return
    issueDetailViewFiredRef.current = true
    trackResearchAgents(ResearchAgentsAction.issue_detail_view, {
      issue_uuid: issueUuid,
      issue_title: issue.title,
      is_severe: issue.is_severe,
      agent_type: ISSUE_DETECTION_AGENT_TYPE,
      agent_uuid: issueItem.agent_uuid,
      agent_name: issueItem.agent_name,
    })
  }, [issueUuid, issue, issueItem])

  // Breadcrumb
  useEffect(() => {
    setBreadcrumbSubtitle(issue?.title ?? null)
    return () => setBreadcrumbSubtitle(null)
  }, [issue?.title, setBreadcrumbSubtitle])

  // Custom breakdowns for dimension selector
  const defaultFilterList = useFilterUIStore(
    (state: FilterUIState) => state.defaultFilterList,
  )
  const customBreakdowns = useMemo(
    () => deriveCustomBreakdowns(defaultFilterList || []),
    [defaultFilterList],
  )

  // Tracking — update the react-query cache after mutation so the single
  // source of truth (issue-detail query) stays correct without shadow state.
  const queryClient = useQueryClient()
  const [trackingLoading, setTrackingLoading] = useState(false)
  const isTracked = issue?.is_tracked ?? false
  const [chartMode, setChartMode] = useState<ChartMode>('share')

  const handleIssueTrackingChange = useCallback(
    async (nextTracked: boolean) => {
      if (!projectId || !issue) return
      setTrackingLoading(true)
      trackResearchAgents(
        nextTracked
          ? ResearchAgentsAction.issue_track_click
          : ResearchAgentsAction.issue_untrack_click,
        {
          issue_uuid: issue.uuid,
          issue_title: issue.title,
          is_severe: issue.is_severe,
          agent_type: ISSUE_DETECTION_AGENT_TYPE,
          agent_uuid: issueItem?.agent_uuid ?? null,
          agent_name: issueItem?.agent_name ?? null,
          source: 'issue_detail',
        },
      )
      try {
        if (nextTracked) {
          const response = await service.trackIssue(projectId, issue.uuid)
          toast.success('Tracking enabled. Find it in your Tracked feed.')
          handleEmailPromptResponse(response, issue.uuid)
        } else {
          await service.untrackIssue(projectId, issue.uuid)
          toast.success('Stopped tracking. Issue removed from Tracked.')
        }
        queryClient.setQueryData<TrendFeedIssueItem | undefined>(
          ['issue-detail', projectId, issueUuid],
          (prev) =>
            prev
              ? {
                  ...prev,
                  issue: { ...prev.issue, is_tracked: nextTracked },
                }
              : prev,
        )
      } catch {
        toast.error('Could not update tracking. Try again.')
      } finally {
        setTrackingLoading(false)
      }
    },
    [
      projectId,
      issue,
      issueItem,
      service,
      queryClient,
      issueUuid,
      handleEmailPromptResponse,
    ],
  )

  // Feedback
  const feedback = useIssueFeedback(service, projectId)

  const handleFeedbackClick = useCallback(
    (issueUuid: string, isPositive: boolean) => {
      trackResearchAgents(ResearchAgentsAction.feedback_modal_open, {
        issue_uuid: issueUuid,
        agent_type: ISSUE_DETECTION_AGENT_TYPE,
        is_positive: isPositive,
        source: 'issue_detail',
      })
      feedback.handleFeedbackClick(issueUuid, isPositive)
    },
    [feedback],
  )

  const handleFeedbackSubmit = useCallback(
    async (isPositive: boolean, additionalDetails: string) => {
      try {
        await feedback.handleFeedbackSubmit(isPositive, additionalDetails)
        if (issueUuid) {
          trackResearchAgents(ResearchAgentsAction.feedback_submit, {
            issue_uuid: issueUuid,
            agent_type: ISSUE_DETECTION_AGENT_TYPE,
            is_positive: isPositive,
            has_additional_details: additionalDetails.trim().length > 0,
            source: 'issue_detail',
          })
        }
        queryClient.setQueryData<TrendFeedIssueItem | undefined>(
          ['issue-detail', projectId, issueUuid],
          (prev) =>
            prev
              ? {
                  ...prev,
                  issue: {
                    ...prev.issue,
                    user_has_submitted_feedback: true,
                    user_feedback_is_positive: isPositive,
                  },
                }
              : prev,
        )
        toast.success('Feedback submitted')
      } catch {
        toast.error('Failed to submit feedback')
      }
    },
    [feedback, queryClient, projectId, issueUuid],
  )

  const handleFeedbackDismiss = useCallback(() => {
    if (issueUuid && feedback.feedbackIsPositive !== null) {
      trackResearchAgents(ResearchAgentsAction.feedback_modal_dismiss, {
        issue_uuid: issueUuid,
        agent_type: ISSUE_DETECTION_AGENT_TYPE,
        is_positive: feedback.feedbackIsPositive,
        source: 'issue_detail',
      })
    }
    feedback.handleFeedbackCancel()
  }, [feedback, issueUuid])

  // Trend chart download
  const trendChartRef = useRef<HTMLDivElement>(null)
  const handleDownloadTrend = useCallback(async () => {
    if (!trendChartRef.current) return
    await downloadChartAsPng(trendChartRef.current, 'mentions-vs-baseline')
    trackResearchAgents(ResearchAgentsAction.insight_chart_download, {
      chart: 'mentions_trend',
      source: 'issue_detail',
    })
  }, [])

  const handleBreakdownDimensionChange = useCallback(
    (dim: IssueBreakdownSelectionValue) => {
      detail.onBreakdownDimensionChange(dim)
      trackResearchAgents(ResearchAgentsAction.breakdown_dimension_change, {
        dimension: dim,
        source: 'issue_detail',
      })
    },
    [detail],
  )

  // Derived values
  const emergenceLevel: EmergenceLevel = issueCard
    ? getEmergenceLevel(issueCard.emergence_score_normalized)
    : 'nothing'
  const emergenceMultiplier =
    issueCard?.emergence_score_normalized === null
      ? Infinity
      : typeof issueCard?.emergence_score_normalized === 'number'
        ? issueCard.emergence_score_normalized
        : null
  const currentShare =
    issueCard?.emergence_score_normalized != null &&
    issueCard?.prior_period_share != null
      ? issueCard.emergence_score_normalized * issueCard.prior_period_share
      : null

  if (!projectId || !issueUuid) return null

  return (
    <>
      <PageShell>
        <InsightDetailSubHeader
          onBack={() => navigate(trendDetectionFeedPath(projectId))}
          backLabel="Back to Feed"
          upvoteActive={
            !!(
              issue?.user_has_submitted_feedback &&
              issue.user_feedback_is_positive === true
            )
          }
          downvoteActive={
            !!(
              issue?.user_has_submitted_feedback &&
              issue.user_feedback_is_positive === false
            )
          }
          onUpvote={
            issue ? () => handleFeedbackClick(issue.uuid, true) : undefined
          }
          onDownvote={
            issue ? () => handleFeedbackClick(issue.uuid, false) : undefined
          }
          feedbackDisabled={!issue}
          isTracked={isTracked}
          onTrackingChange={handleIssueTrackingChange}
          trackingLoading={trackingLoading}
          trackingDisabled={!issue}
          onShare={() =>
            trackResearchAgents(ResearchAgentsAction.insight_share_click, {
              source: 'issue_detail',
            })
          }
        />
        <PageContainer>
          <ContentWrapper>
            {/* Header skeleton */}
            {detail.issueLoading && (
              <HeaderSkeleton>
                <Skeleton active paragraph={{ rows: 3 }} />
              </HeaderSkeleton>
            )}

            {detail.issueError && !detail.issueLoading && (
              <ErrorSection>
                Issue not found or could not be loaded.
              </ErrorSection>
            )}

            {issue && issueCard && (
              <>
                {/* Header — order matches the prototype: detection badge,
                  title, description, then agent meta. */}
                <InsightHeader>
                  <DetectionBadgeRow>
                    <DetectionStatusIconBox $level={emergenceLevel}>
                      <DetectedIssueIcon
                        color={EMERGENCE_ICON_COLOR[emergenceLevel]}
                        width={24}
                        height={24}
                      />
                    </DetectionStatusIconBox>
                    <DetectionLabels>
                      <DetectionTitle>Issue Detected</DetectionTitle>
                      {emergenceLevel !== 'nothing' && (
                        <EmergenceChipRow>
                          <EmergenceBarChart level={emergenceLevel} />
                          <EmergenceChipLabel
                            $color={EMERGENCE_COLORS[emergenceLevel]}
                          >
                            {emergenceLevel
                              .replace(/_/g, ' ')
                              .replace(/^\w/, (c) => c.toUpperCase())}
                          </EmergenceChipLabel>
                          <ElevationLevelPopover
                            tiers={EMERGENCE_TIERS}
                            activeTierKey={emergenceLevel}
                            share={currentShare}
                            comparisonMultiplier={emergenceMultiplier}
                          />
                          {issueCard?.is_severe && <SafetyIndicator />}
                        </EmergenceChipRow>
                      )}
                      {emergenceLevel === 'nothing' && issueCard?.is_severe && (
                        <EmergenceChipRow>
                          <SafetyIndicator />
                        </EmergenceChipRow>
                      )}
                    </DetectionLabels>
                  </DetectionBadgeRow>
                  <InsightTitle>
                    {issueCard.title || 'Untitled Issue'}
                  </InsightTitle>
                  {issueCard.description && (
                    <InsightDescription>
                      {issueCard.description}
                    </InsightDescription>
                  )}
                  <IssueAgentMetaRow
                    projectId={projectId}
                    agentUuid={issueItem?.agent_uuid ?? null}
                    agentName={issueItem?.agent_name ?? null}
                    periodLabel={(() => {
                      const period = formatOriginalAnalysisPeriod(
                        issue.detection_period_start_date ?? null,
                        issue.detection_period_end_date ?? null,
                      )
                      return period ? `Detected ${period}` : null
                    })()}
                    updatedDateLabel={
                      issue.latest_update_create_time
                        ? formatUtcDateFromISO(issue.latest_update_create_time)
                        : null
                    }
                  />
                </InsightHeader>

                {/* Issue summary + Current signals */}
                {issueCard?.takeaways?.at_a_glance && (
                  <SectionCard>
                    <GlanceColumns>
                      <GlancePanel>
                        <CardTitle>Issue summary</CardTitle>
                        <TakeawayList>
                          {issueCard.takeaways.at_a_glance.takeaways.map(
                            (t, i) => (
                              <TakeawayItem key={i}>{t}</TakeawayItem>
                            ),
                          )}
                        </TakeawayList>
                      </GlancePanel>
                      <GlancePanelDivider />
                      <GlancePanel>
                        <CardTitle>Current signals</CardTitle>
                        <TakeawayList>
                          {issueCard.takeaways.at_a_glance.key_signals.map(
                            (s, i) => (
                              <TakeawayItem key={i}>
                                {renderWithBoldMetrics(s)}
                              </TakeawayItem>
                            ),
                          )}
                        </TakeawayList>
                      </GlancePanel>
                    </GlanceColumns>
                  </SectionCard>
                )}

                {/* Mentions vs. baseline average */}
                <SectionCard>
                  <CardHeader>
                    <CardTitle>Mentions vs. baseline average</CardTitle>
                    <CardActions>
                      <ChartModeButtonGroup>
                        <ChartModeButton
                          type="button"
                          $active={chartMode === 'share'}
                          $first
                          onClick={() => setChartMode('share')}
                        >
                          Share
                        </ChartModeButton>
                        <ChartModeButton
                          type="button"
                          $active={chartMode === 'count'}
                          onClick={() => setChartMode('count')}
                        >
                          Count
                        </ChartModeButton>
                      </ChartModeButtonGroup>
                      <CardIconButton
                        onClick={handleDownloadTrend}
                        aria-label="Download chart"
                      >
                        <FileDownloadOutlined style={{ fontSize: 14 }} />
                      </CardIconButton>
                    </CardActions>
                  </CardHeader>
                  {detail.timeseriesLoading && (
                    <ChartLoadingContainer>
                      <ChartBarSkeleton />
                    </ChartLoadingContainer>
                  )}
                  {!detail.timeseriesLoading &&
                    detail.timeseriesChart &&
                    detail.timeseriesChart.data?.length > 0 && (
                      <ChartDownloadWrap ref={trendChartRef}>
                        <ChartContainer>
                          <IssueTimeseriesChart
                            issueChart={detail.timeseriesChart}
                            projectTotalChart={detail.projectTotalChart}
                            baselineShare={issueCard?.prior_period_share}
                            mode={chartMode}
                            height="280px"
                          />
                        </ChartContainer>
                      </ChartDownloadWrap>
                    )}
                  {!detail.timeseriesLoading &&
                    (!detail.timeseriesChart ||
                      !detail.timeseriesChart.data?.length) && (
                      <NoChartData>No timeseries data available</NoChartData>
                    )}
                </SectionCard>

                <BreakdownSection
                  breakdownChart={detail.breakdownChart}
                  breakdownLoading={detail.breakdownLoading}
                  selectedBreakdownDimension={detail.selectedBreakdownDimension}
                  customBreakdowns={customBreakdowns}
                  onBreakdownDimensionChange={handleBreakdownDimensionChange}
                  onDownload={() =>
                    trackResearchAgents(
                      ResearchAgentsAction.insight_chart_download,
                      {
                        chart: 'impact_area_breakdown',
                        source: 'issue_detail',
                      },
                    )
                  }
                />

                <VerbatimsSection
                  postTexts={detail.postTexts}
                  verbatimsLoading={detail.verbatimsLoading}
                  projectId={projectId}
                  feedbackFilterHash={issue.feedback_filter_hash ?? null}
                  onViewAllRecords={() =>
                    trackResearchAgents(
                      ResearchAgentsAction.view_all_records_click,
                      { source: 'issue_detail' },
                    )
                  }
                />
              </>
            )}
          </ContentWrapper>
        </PageContainer>
      </PageShell>

      <IssueFeedbackModal
        isVisible={feedback.feedbackModalVisible}
        isPositive={feedback.feedbackIsPositive}
        onSubmit={handleFeedbackSubmit}
        onCancel={handleFeedbackDismiss}
        isLoading={feedback.submittingFeedback}
      />
      <TrackEmailOptInModal
        isOpen={emailPrompt.isOpen}
        agentName={emailPrompt.agentName}
        loading={emailPrompt.loading}
        onOptIn={emailPrompt.onOptIn}
        onDecline={emailPrompt.onDecline}
      />
    </>
  )
}

// ─── Page-local styled components ───────────────────────────────────────────

const HeaderSkeleton = styled.div`
  padding: 24px;
  background: white;
  border: 1px solid ${cardBorder};
  border-radius: 8px;
`

const ErrorSection = styled.div`
  padding: 40px 24px;
  text-align: center;
  font-size: 15px;
  color: ${darkBlueFontColor};
  opacity: 0.7;
`

const DetectionStatusIconBox = styled.span<{ $level: EmergenceLevel }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 7px;
  flex-shrink: 0;
  border-radius: 5px;
  background: ${({ $level }) => EMERGENCE_BOX_BG[$level]};
  border: 0.86px solid ${({ $level }) => EMERGENCE_BOX_BORDER[$level]};
`

const EmergenceChipRow = styled.span`
  display: flex;
  align-items: center;
  gap: 4px;
`

const EmergenceChipLabel = styled.span<{ $color: string }>`
  font-family: var(--Font-family-font-family-body, Poppins), serif;
  font-size: 12px;
  font-weight: 600;
  line-height: 16px;
  color: ${({ $color }) => $color};
`

const ChartDownloadWrap = styled.div`
  background: white;
  padding: 8px 0 0;
`
